import { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useForm, Controller } from "react-hook-form";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useState, useEffect } from "react";
import { apiCall } from "../Apis/apiCall";
import { useNavigate } from "react-router-dom";
import LoadingComponent from "../components/ui/LoadingComponent";
import { authApi } from "../Apis/Api";
import { usePermissions } from "../context/PermissionContext";
import { LOGO_URL, PROFILE_PICTURE_URL } from "../utils/Contants";
import Breadcrumb from "../components/Breadcrumb";
import ErrorAlert from "../components/ui/ErrorAlert";
import { validateImageFile } from "../utils/validateImageFile";
import CustomPhoneInput from "../components/common/CustomPhoneInput";
import { toast } from "react-toastify";
import CustomSelect from "../components/common/CustomSelect";
import { handleApiResponse } from "../utils/response";
import parsePhoneNumberFromString from "libphonenumber-js";

// Validation Schema (Client Type REMOVED)
const profileValidationSchema = Yup.object().shape({
  name: Yup.string()
    .required("Full Name is required")
    .min(2, "Full Name must be at least 2 characters")
    .max(20, "Full Name must not exceed 20 characters"),
  companyName: Yup.string().when("$isSuperAdmin", {
    is: true,
    then: () =>
      Yup.string()
        .required("Company Name is required")
        .max(50, "Company Name must not exceed 50 characters"),
    otherwise: () => Yup.string().notRequired(),
  }),
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  phone: Yup.string()
    .required("Phone number is required")
    .test("is-valid-phone", "Invalid phone number", (value) =>
      value ? isValidPhoneNumber(value) : false
    ),
  code: Yup.string().when("$isSuperAdmin", {
    is: true,
    then: () => Yup.string().required("Currency is required"),
    otherwise: () => Yup.string().notRequired(),
  }),
});

const passwordValidationSchema = Yup.object().shape({
  oldPassword: Yup.string().required("Old Password is required"),
  newPassword: Yup.string()
    .required("New Password is required")
    .min(8, "Password must be at least 8 characters")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
      "Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character"
    ),
  confirmPassword: Yup.string()
    .required("Confirm Password is required")
    .oneOf([Yup.ref("newPassword"), null], "Passwords must match"),
});

const ProfilePage = () => {
  const [logoPreview, setLogoPreview] = useState(null);
  const [profilePicPreview, setProfilePicPreview] = useState(null);
  const [staffData, setStaffData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imageUploading, setImageUploading] = useState({
    logo: false,
    profile: false,
  });
  const [error, setError] = useState(null);
  const [oldPasswordVisible, setOldPasswordVisible] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [currency, setCurrency] = useState({
    currency_code: "",
    currency_symbol: "",
  });
  const [nameLength, setNameLength] = useState(0);
  const [companyNameLength, setCompanyLength] = useState(0);
  const { refreshAuthStatus } = usePermissions();
  const navigate = useNavigate();

  // Get from localStorage
  const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
  const staff_id = storedUser?.staff_id || null;
  const isSuperAdmin = storedUser?.is_client_admin || false;

  const updateEndpoint = isSuperAdmin
    ? "/update-client-profile"
    : `/update-staff/${staff_id}`;

  // Form Hooks
  const {
    control: profileControl,
    handleSubmit: handleProfileSubmit,
    setValue,
    formState: { errors: profileErrors },
  } = useForm({
    resolver: yupResolver(profileValidationSchema),
    defaultValues: {
      name: "",
      companyName: "",
      email: "",
      phone: "",
      code: "",
    },
    context: { isSuperAdmin },
  });

  const {
    control: passwordControl,
    handleSubmit: handlePasswordSubmit,
    reset: resetPasswordForm,
    formState: { errors: passwordErrors },
  } = useForm({
    resolver: yupResolver(passwordValidationSchema),
    defaultValues: {
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  // Fetch Currency Codes
  useEffect(() => {
    const fetchCurrencyCodes = async () => {
      try {
        const res = await apiCall(
          "get",
          "/get-currency-codes?want_pagination=0"
        );
        if (res.data.success) {
          const preferredSymbols = { AUD: "A$", USD: "$" };
          const uniqueCurrencies = Array.from(
            new Map(
              res.data.data
                .sort((a, b) => {
                  if (a.currency_code === b.currency_code) {
                    return preferredSymbols[a.currency_code] ===
                      a.currency_symbol
                      ? -1
                      : 1;
                  }
                  return 0;
                })
                .map((c) => [c.currency_code, c])
            ).values()
          );
          const options = uniqueCurrencies.map((c) => ({
            label: `${c.currency_code} (${c.currency_symbol})`,
            value: c.currency_code,
            raw: c,
          }));
          setCurrencyOptions(options);
        }
      } catch (err) {
        console.error("Currency fetch error:", err);
      }
    };
    fetchCurrencyCodes();
  }, []);

  // Fetch Staff Data
  const fetchStaffData = async () => {
    if (!staff_id) {
      setError("Staff ID not found. Please login again.");
      setLoading(false);
      navigate("/sign-in");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await apiCall("get", `/get-staff-by-id/${staff_id}`);

      if (response.data.success) {
        const result = response.data.data;
        localStorage.setItem("user", JSON.stringify(result));
        localStorage.setItem("country_code", result?.country_code);

        setStaffData(result);

        // Fill Form
        setValue("name", result.staff_name || "");
        setValue("companyName", result.company_name || "");
        setValue("email", result.staff_email || "");
        setValue("phone", result.staff_phone || "");
        setValue("code", result.currency_code || "");
setNameLength((result.staff_name || "").trim().length);
setCompanyLength((result.company_name || "").trim().length);
        // Profile Picture
        if (result.profile_picture) {
          setProfilePicPreview(
            result.profile_picture.startsWith("http")
              ? result.profile_picture
              : `${PROFILE_PICTURE_URL}/${result.profile_picture}`
          );
        } else {
          setProfilePicPreview("assets/images/user-grid/user.png");
        }

        // Logo (Super Admin)
        if (isSuperAdmin && result.logo_url) {
          setLogoPreview(`${LOGO_URL}/${result.logo_url}`);
        }

        // Currency State Sync
        if (result.currency_code && result.currency_symbol) {
          setCurrency({
            currency_code: result.currency_code,
            currency_symbol: result.currency_symbol,
          });
        }
      } else {
        toast.error("Failed to load profile");
        setError("Failed to load profile");
      }
    } catch (err) {
      console.error("API Error:", err);
      toast.error("Error loading profile");
      setError("Failed to connect to server");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchStaffData();
  }, [staff_id, setValue, isSuperAdmin, navigate]);

  // Image Upload Handler
  const readURL = async (input, type) => {
    if (!input.target.files?.[0]) return;

    const file = input.target.files[0];
    setImageUploading((prev) => ({ ...prev, [type]: true }));

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        if (type === "logo") await validateImageFile(file);

        let response, newImageUrl;

        if (type === "logo") {
          setLogoPreview(e.target.result);
          response = await authApi.logoImage(file);
          newImageUrl = `${LOGO_URL}/${response.tiny_url}`;
        } else {
          setProfilePicPreview(e.target.result);
          response = await authApi.profileImage(file);
          newImageUrl = `${PROFILE_PICTURE_URL}/${response.tiny_url}`;
        }

        const updatedData = {
          ...staffData,
          [type === "logo" ? "logo_url" : "profile_picture"]: response.tiny_url,
        };
        setStaffData(updatedData);

        if (type === "logo") setLogoPreview(newImageUrl);
        else setProfilePicPreview(newImageUrl);

        localStorage.setItem(
          "user",
          JSON.stringify({
            ...storedUser,
            [type === "logo" ? "logo_url" : "profile_picture"]:
              response.tiny_url,
          })
        );

        toast.success(
          `${type === "logo" ? "Logo" : "Profile picture"} uploaded!`
        );
      } catch (err) {
        toast.error(
          `${type === "logo" ? "Logo" : "Profile picture"} upload failed!`
        );
        setLogoPreview(
          staffData?.logo_url ? `${LOGO_URL}/${staffData.logo_url}` : null
        );
        setProfilePicPreview(
          staffData?.profile_picture
            ? `${PROFILE_PICTURE_URL}/${staffData.profile_picture}`
            : "assets/images/user-grid/user.png"
        );
      } finally {
        setImageUploading((prev) => ({ ...prev, [type]: false }));
      }
    };

    reader.readAsDataURL(file);
  };

  // Profile Submit
  const onProfileSubmit = async (data) => {
    if (!staffData) return;
    try {
      setLoading(true);
      let countryCodeToSend = null;
      if (isSuperAdmin) {
        if (data.phone) {
          const parsed = parsePhoneNumberFromString(data.phone);
          console.log("parsed", parsed);
          if (parsed?.country) {
            const code = parsed.country.toLowerCase();
            countryCodeToSend = code;
            // localStorage.setItem("country_code", code);
          }
        }
      }

      const payload = isSuperAdmin
        ? {
            client_name: data.name,
            company_name: data.companyName,
            client_email: data.email,
            client_phone_no: data.phone,
            logo_url: staffData.logo_url || null,
            profile_picture: staffData.profile_picture || null,
            currency_code: currency.currency_code,
            currency_symbol: currency.currency_symbol,
            country_code: countryCodeToSend || null,
          }
        : {
            staff_name: data.name,
            staff_email: data.email,
            staff_phone_no: data.phone,
            profile_picture: staffData.profile_picture || null,
            // country_code: countryCodeToSend || null,
          };
      const response = await apiCall("put", updateEndpoint, payload);

      if (response.data.success) {
        await fetchStaffData();

        toast.success(response.data.message || "Profile updated successfully!");
        await refreshAuthStatus();
        navigate("/dashboard");
      }
    } catch (err) {
      console.error("Profile update error:", err);
      toast.error(err?.message || "Profile update failed!");
    } finally {
      setLoading(false);
    }
  };

  // Password Submit
  const onPasswordSubmit = async (data) => {
    console.log(data);
    try {
      setLoading(true);
      const response = await apiCall("put", "/change-password", {
        // email: staffData?.staff_email,
        new_password: data.newPassword,
        old_password: data?.oldPassword,
        // product_id: 1,
      });

      if (response.data.success) {
        // toast.success("Password updated successfully!");
        handleApiResponse(
          response.data,
          "Password updated successfully!",
          "PUT"
        );
        resetPasswordForm();
      }
      await refreshAuthStatus();
    } catch (error) {
      handleApiResponse(error, "Something went wrong", "PUT");
    } finally {
      setLoading(false);
    }
  };

  // UI Helpers
  const toggleOldPasswordVisibility = () =>
    setOldPasswordVisible(!oldPasswordVisible);
  const togglePasswordVisibility = () => setPasswordVisible(!passwordVisible);
  const toggleConfirmPasswordVisibility = () =>
    setConfirmPasswordVisible(!confirmPasswordVisible);
  const handleCurrencyChange = (selected) => {
    if (selected) {
      console.log(selected);
      setCurrency({
        currency_code: selected.raw.raw.currency_code,
        currency_symbol: selected.raw.raw.currency_symbol,
      });
    } else {
      setCurrency({ currency_code: "", currency_symbol: "" });
    }
  };

  // Render
  if (error) return <ErrorAlert message={error} />;
  if (loading && !staffData) return <LoadingComponent />;

  return (
    <>
      <Breadcrumb title="Profile" />
      <div className="row gy-4">
        {/* Left Card - Profile Info */}
        <div className="col-lg-4">
          <div className="user-grid-card position-relative border radius-16 overflow-hidden bg-base h-100">
            <div className="pb-24 ms-16 mb-24 me-16">
              <div className="text-center border border-top-0 border-start-0 border-end-0 pb-16">
                <img
                  src={profilePicPreview || "assets/images/user-grid/user.png"}
                  alt="Profile"
                  className="border br-white border-width-2-px w-200-px h-200-px rounded-circle object-fit-cover"
                />
                <h6 className="mb-0 mt-16">
                  {staffData?.staff_name || "User"}
                </h6>
                <span className="text-secondary-light mb-8 d-block fw-medium">
                  {staffData?.company_name || "No Company"}
                </span>
                <span className="text-secondary-light">
                  {staffData?.staff_email || ""}
                </span>
              </div>

              <div className="mt-24">
                <h6 className="text-xl mb-16">Personal Info</h6>
                <ul>
                  <li className="d-flex align-items-center gap-1 mb-12">
                    <span className="w-30 text-md fw-semibold text-primary-light">
                      Company Name
                    </span>
                    <span className="w-70 text-secondary-light fw-medium">
                      {staffData?.company_name || "-"}
                    </span>
                  </li>
                  <li className="d-flex align-items-center gap-1 mb-12">
                    <span className="w-30 text-md fw-semibold text-primary-light">
                      Full Name
                    </span>
                    <span className="w-70 text-secondary-light fw-medium">
                      {staffData?.staff_name || "-"}
                    </span>
                  </li>
                  <li className="d-flex align-items-center gap-1 mb-12">
                    <span className="w-30 text-md fw-semibold text-primary-light">
                      Email
                    </span>
                    <span className="w-70 text-secondary-light fw-medium">
                      {staffData?.staff_email || "-"}
                    </span>
                  </li>
                  <li className="d-flex align-items-center gap-1 mb-12">
                    <span className="w-30 text-md fw-semibold text-primary-light">
                      Phone
                    </span>
                    <span className="w-70 text-secondary-light fw-medium">
                      {staffData?.staff_phone || "-"}
                    </span>
                  </li>
                  <li className="d-flex align-items-center gap-1 mb-12">
                    <span className="w-30 text-md fw-semibold text-primary-light">
                      Role
                    </span>
                    <span className="w-70 text-secondary-light fw-medium">
                      {staffData?.role_name || "-"}
                    </span>
                  </li>
                  {isSuperAdmin && (
                    <>
                      <li className="d-flex align-items-center gap-1 mb-12">
                        <span className="w-30 text-md fw-semibold text-primary-light">
                          Plan
                        </span>
                        <span className="w-70 text-secondary-light fw-medium">
                          {staffData?.plan_name || "-"}
                        </span>
                      </li>
                      <li className="d-flex align-items-center gap-1 mb-12">
                        <span className="w-30 text-md fw-semibold text-primary-light">
                          Plan Start Date
                        </span>
                        <span className="w-70 text-secondary-light fw-medium">
                          {staffData?.plan_start_date
                            ? new Date(
                                staffData.plan_start_date
                              ).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              })
                            : "-"}
                        </span>
                      </li>
                      <li className="d-flex align-items-center gap-1 mb-12">
                        <span className="w-30 text-md fw-semibold text-primary-light">
                          Plan End Date
                        </span>
                        <span className="w-70 text-secondary-light fw-medium">
                          {staffData?.plan_end_date
                            ? new Date(
                                staffData.plan_end_date
                              ).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              })
                            : "-"}
                        </span>
                      </li>
                    </>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Right Tabs */}
        <div className="col-lg-8">
          <div className="card h-100">
            <div className="card-body p-24">
              <ul
                className="nav border-gradient-tab nav-pills mb-20 d-inline-flex"
                id="pills-tab"
                role="tablist"
              >
                <li className="nav-item" role="presentation">
                  <button
                    className="nav-link d-flex align-items-center px-24 active"
                    id="pills-edit-profile-tab"
                    data-bs-toggle="pill"
                    data-bs-target="#pills-edit-profile"
                    type="button"
                    role="tab"
                  >
                    Edit Profile
                  </button>
                </li>
                <li className="nav-item" role="presentation">
                  <button
                    className="nav-link d-flex align-items-center px-24"
                    id="pills-change-passwork-tab"
                    data-bs-toggle="pill"
                    data-bs-target="#pills-change-passwork"
                    type="button"
                    role="tab"
                  >
                    Change Password
                  </button>
                </li>
              </ul>

              <div className="tab-content" id="pills-tabContent">
                {/* Edit Profile Tab */}
                <div
                  className="tab-pane fade show active"
                  id="pills-edit-profile"
                  role="tabpanel"
                >
                  <h6 className="text-md text-primary-light mb-16">
                    Profile Image
                  </h6>
                  <div className="mb-24 mt-16">
                    <div className="avatar-upload">
                      <div className="avatar-edit position-absolute bottom-0 end-0 me-24 mt-16 z-1 cursor-pointer">
                        <input
                          type="file"
                          id="profilePicUpload"
                          accept=".png, .jpg, .jpeg"
                          hidden
                          onChange={(e) => readURL(e, "profile")}
                          disabled={imageUploading.profile}
                        />
                        <label
                          htmlFor="profilePicUpload"
                          className="w-32-px h-32-px d-flex justify-content-center align-items-center bg-primary-600 text-white text-lg rounded-circle shadow-sm"
                          style={{
                            cursor: imageUploading.profile
                              ? "not-allowed"
                              : "pointer",
                            opacity: imageUploading.profile ? 0.7 : 1,
                          }}
                        >
                          {imageUploading.profile ? (
                            <div
                              className="spinner-border spinner-border-sm text-white"
                              role="status"
                              style={{ width: "16px", height: "16px" }}
                            >
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </div>
                          ) : (
                            <Icon
                              icon="solar:camera-outline"
                              className="icon"
                            />
                          )}
                        </label>
                      </div>
                      <div className="avatar-preview">
                        <div
                          id="imagePreview"
                          style={{
                            backgroundImage: `url(${
                              profilePicPreview ||
                              "assets/images/user-grid/user.png"
                            })`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <form onSubmit={handleProfileSubmit(onProfileSubmit)}>
                    <div className="row">
                      {/* Company Name - Super Admin Only */}
                      {isSuperAdmin && (
                        <div className="col-sm-6">
                          <div className="mb-20">
                            <label
                              htmlFor="companyName"
                              className="form-label fw-semibold text-primary-light text-sm mb-8"
                            >
                              Company Name{" "}
                              <span className="text-danger-600">*</span>
                            </label>
                            <Controller
                              name="companyName"
                              control={profileControl}
                              render={({ field }) => (
                                <>
                                  <input
                                    type="text"
                                    className={`form-control radius-8 ${
                                      profileErrors.companyName
                                        ? "is-invalid"
                                        : ""
                                    }`}
                                    id="companyName"
                                    placeholder="Enter Company Name"
                                    {...field}
                                    maxLength={50}
                                    onChange={(e) => {
                                      field.onChange(e);
                                      setCompanyLength(e.target.value.length);
                                    }}
                                  />
                                  <div className="text-end mt-8">
                                    <small
                                      className={`text-${
                                        companyNameLength >= 50
                                          ? "danger"
                                          : "secondary-light"
                                      }`}
                                    >
                                      {companyNameLength}/50
                                    </small>
                                  </div>
                                </>
                              )}
                            />
                            {profileErrors.companyName && (
                              <div className="invalid-feedback">
                                {profileErrors.companyName.message}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Full Name */}
                      <div className="col-sm-6">
                        <div className="mb-20">
                          <label
                            htmlFor="name"
                            className="form-label fw-semibold text-primary-light text-sm mb-8"
                          >
                            Full Name <span className="text-danger-600">*</span>
                          </label>
                          <Controller
                            name="name"
                            control={profileControl}
                            render={({ field }) => (
                              <>
                                <input
                                  type="text"
                                  className={`form-control radius-8 ${
                                    profileErrors.name ? "is-invalid" : ""
                                  }`}
                                  id="name"
                                  placeholder="Enter Full Name"
                                  {...field}
                                  maxLength={20}
                                  onChange={(e) => {
                                    field.onChange(e);
                                    setNameLength(e.target.value.length);
                                  }}
                                />
                                <div className="text-end mt-8">
                                  <small
                                    className={`text-${
                                      nameLength >= 20
                                        ? "danger"
                                        : "secondary-light"
                                    }`}
                                  >
                                    {nameLength}/20
                                  </small>
                                </div>
                              </>
                            )}
                          />
                          {profileErrors.name && (
                            <div className="invalid-feedback">
                              {profileErrors.name.message}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Phone */}
                      <div className="col-md-6 mb-20">
                        <CustomPhoneInput
                          name="phone"
                          control={profileControl}
                          errors={profileErrors}
                          defaultCountry="SA"
                          label="Phone Number"
                          placeholder="Enter phone number"
                        />
                      </div>

                      {/* Currency - Super Admin Only */}
                      {isSuperAdmin && (
                        <div className="col-md-6 mb-20">
                          <label
                            htmlFor="code"
                            className="form-label fw-semibold text-primary-light text-sm mb-8"
                          >
                            Currency <span className="text-danger-600">*</span>
                          </label>
                          <Controller
                            name="code"
                            control={profileControl}
                            render={({ field }) => (
                              <>
                                <CustomSelect
                                  name="code"
                                  value={
                                    currencyOptions.find(
                                      (opt) => opt.value === field.value
                                    ) || null
                                  }
                                  onChange={(selected) => {
                                    field.onChange(
                                      selected ? selected.value : ""
                                    );
                                    handleCurrencyChange(selected);
                                  }}
                                  options={currencyOptions}
                                  isSearchable={true}
                                  isClearable={true}
                                  isMulti={false}
                                  labelKey="label"
                                  valueKey="value"
                                  isLoading={currencyOptions.length === 0}
                                  placeholder={
                                    currencyOptions.length === 0
                                      ? "Loading..."
                                      : "Select Currency"
                                  }
                                />
                                {profileErrors.code && (
                                  <div className="invalid-feedback d-block">
                                    {profileErrors.code.message}
                                  </div>
                                )}
                              </>
                            )}
                          />
                        </div>
                      )}

                      {/* Logo Upload - Super Admin Only */}
                      {isSuperAdmin && (
                        <div className="col-sm-6">
                          <div className="mb-20">
                            <label
                              htmlFor="logoUpload"
                              className="form-label fw-semibold text-primary-light text-sm mb-8"
                            >
                              Company Logo
                            </label>
                            <div className="avatar-upload">
                              <div className="avatar-edit">
                                <input
                                  type="file"
                                  id="logoUpload"
                                  accept=".png, .jpg, .jpeg"
                                  hidden
                                  onChange={(e) => readURL(e, "logo")}
                                  disabled={imageUploading.logo}
                                />
                                <label
                                  htmlFor="logoUpload"
                                  className="btn btn-outline-primary-600 text-md px-24 py-8 radius-8 d-flex align-items-center"
                                >
                                  {imageUploading.logo ? (
                                    <>
                                      <div
                                        className="spinner-border spinner-border-sm me-8 text-primary-600"
                                        role="status"
                                      >
                                        <span className="visually-hidden">
                                          Loading...
                                        </span>
                                      </div>
                                      Uploading...
                                    </>
                                  ) : (
                                    <>
                                      <Icon
                                        icon="solar:upload-outline"
                                        className="me-8"
                                      />
                                      Choose File
                                    </>
                                  )}
                                </label>
                              </div>
                              {logoPreview && (
                                <div className="mt-8">
                                  <img
                                    src={logoPreview}
                                    alt="Logo Preview"
                                    className="w-100-px h-100-px object-fit-cover rounded"
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="d-flex align-items-center justify-content-center gap-3">
                      <button
                        type="button"
                        className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-56 py-11 radius-8"
                        onClick={() => navigate(-1)}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary-600 border border-primary-600 text-md px-56 py-12 radius-8"
                        disabled={
                          imageUploading.logo ||
                          imageUploading.profile ||
                          loading
                        }
                      >
                        {imageUploading.logo ||
                        imageUploading.profile ||
                        loading
                          ? "Please wait..."
                          : "Save"}
                      </button>
                    </div>
                  </form>
                </div>

                {/* Change Password Tab */}
                <div
                  className="tab-pane fade"
                  id="pills-change-passwork"
                  role="tabpanel"
                >
                  <form onSubmit={handlePasswordSubmit(onPasswordSubmit)}>
                    <div className="mb-20">
                      <label
                        htmlFor="newPassword"
                        className="form-label fw-semibold text-primary-light text-sm mb-8"
                      >
                        Old Password <span className="text-danger-600">*</span>
                      </label>
                      <div className="position-relative">
                        <Controller
                          name="oldPassword"
                          control={passwordControl}
                          render={({ field }) => (
                            <input
                              type={oldPasswordVisible ? "text" : "password"}
                              className={`form-control radius-8 ${
                                passwordErrors.oldPassword ? "is-invalid" : ""
                              }`}
                              id="oldPassword"
                              placeholder="Enter Old Password*"
                              {...field}
                            />
                          )}
                        />
                        <span
                          className={`toggle-password ${
                            oldPasswordVisible
                              ? "ri-eye-off-line"
                              : "ri-eye-line"
                          } cursor-pointer position-absolute end-0 top-50 translate-middle-y me-16 text-secondary-light`}
                          onClick={toggleOldPasswordVisibility}
                        ></span>
                        {passwordErrors.oldPassword && (
                          <div className="invalid-feedback">
                            {passwordErrors.oldPassword.message}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="mb-20">
                      <label
                        htmlFor="newPassword"
                        className="form-label fw-semibold text-primary-light text-sm mb-8"
                      >
                        New Password <span className="text-danger-600">*</span>
                      </label>
                      <div className="position-relative">
                        <Controller
                          name="newPassword"
                          control={passwordControl}
                          render={({ field }) => (
                            <input
                              type={passwordVisible ? "text" : "password"}
                              className={`form-control radius-8 ${
                                passwordErrors.newPassword ? "is-invalid" : ""
                              }`}
                              id="newPassword"
                              placeholder="Enter New Password*"
                              {...field}
                            />
                          )}
                        />
                        <span
                          className={`toggle-password ${
                            passwordVisible ? "ri-eye-off-line" : "ri-eye-line"
                          } cursor-pointer position-absolute end-0 top-50 translate-middle-y me-16 text-secondary-light`}
                          onClick={togglePasswordVisibility}
                        ></span>
                        {passwordErrors.newPassword && (
                          <div className="invalid-feedback">
                            {passwordErrors.newPassword.message}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="mb-20">
                      <label
                        htmlFor="confirmPassword"
                        className="form-label fw-semibold text-primary-light text-sm mb-8"
                      >
                        Confirm Password{" "}
                        <span className="text-danger-600">*</span>
                      </label>
                      <div className="position-relative">
                        <Controller
                          name="confirmPassword"
                          control={passwordControl}
                          render={({ field }) => (
                            <input
                              type={
                                confirmPasswordVisible ? "text" : "password"
                              }
                              className={`form-control radius-8 ${
                                passwordErrors.confirmPassword
                                  ? "is-invalid"
                                  : ""
                              }`}
                              id="confirmPassword"
                              placeholder="Confirm Password*"
                              {...field}
                            />
                          )}
                        />
                        <span
                          className={`toggle-password ${
                            confirmPasswordVisible
                              ? "ri-eye-off-line"
                              : "ri-eye-line"
                          } cursor-pointer position-absolute end-0 top-50 translate-middle-y me-16 text-secondary-light`}
                          onClick={toggleConfirmPasswordVisibility}
                        ></span>
                        {passwordErrors.confirmPassword && (
                          <div className="invalid-feedback">
                            {passwordErrors.confirmPassword.message}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="d-flex align-items-center justify-content-center gap-3">
                      <button
                        type="button"
                        className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-56 py-11 radius-8"
                        onClick={() => navigate(-1)}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary-600 border border-primary-600 text-md px-56 py-12 radius-8"
                        disabled={loading}
                      >
                        {loading ? "Please wait..." : "Save"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProfilePage;
